import React, {memo, useMemo, useState, useRef, useEffect} from 'react';
import {
  View,
  Image,
  Pressable,
  TouchableOpacity,
  Clipboard,
  Dimensions,
  LayoutChangeEvent,
  Animated,
  Easing,
  StyleSheet,
  Linking,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Carousel from 'react-native-reanimated-carousel';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import CCText from '@/Atoms/CCText';
import Shine from '@/Atoms/Shine';
import NudgeArrow from '@/Atoms/NudgeArrow';
import Box from '@/Atoms/Box';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {styles} from './styles';
import {DASHBOARD_BANNERS, DynamicBannerConfig} from './config';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';
import {useNavigation} from '@react-navigation/native';
import useGetNextMasteryTournament from '@/Hooks/MasterySeries/useGetNextMasteryTournament';
import useGetUpcomingArena from '@/Hooks/TournamentsApi/useGetUpcomingArena';
import {useRoyaleTournaments} from '@/Hooks/FunChallengesApi/useRoyaleTournaments';
import {useLeaderboardData} from '@/Screens/Leaderboard/useLeaderboardData';
import {ScreenName} from '@/navigators/StackConstants';
import {dsIcons} from '../../designIcons';
import RadarDot from '@/Atoms/RadarDot';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import CampaignBanner from './CampaignBanner';
import {
  ARENA_CAMPAIGN,
  ARENA_GENERIC,
  PUZZLE_ROYALE,
  WINE,
  INK_GOLD,
} from './campaignConfig';

const {width: screenWidth} = Dimensions.get('window');

const ShimmerOverlay = () => {
  const translateX = useRef(new Animated.Value(-screenWidth))?.current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(translateX, {
          toValue: screenWidth * 1.5,
          duration: 3500,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.delay(500),
      ]),
    ).start();
  }, [translateX]);

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        StyleSheet.absoluteFillObject,
        {
          width: '40%',
          height: '100%',
          opacity: 0.15,
          zIndex: 0,
          transform: [{translateX}, {skewX: '-25deg'}],
        },
      ]}>
      <LinearGradient
        colors={[
          'transparent',
          'rgba(255, 255, 255, 0.02)',
          'rgba(255, 255, 255, 0.6)',
          'rgba(255, 255, 255, 0.02)',
          'transparent',
        ]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={{flex: 1}}
      />
    </Animated.View>
  );
};

interface BannerCarouselProps {
  // Persona + real data for the persona-driven info-banner rotation — design
  // source: DashboardRevampContent.dc.html's `bannerSets` (self_learner:
  // [review, assess, board], first_timer: [firstGame, board, assess],
  // csoc_class_day: [classToday, review], csoc_rest_day: [nextClass, review]).
  // The CSOC class slides and the Play slide have both since moved out of the
  // carousel into cards of their own — see FocusCards and QuickActions.
  isBeginnerPlayer?: boolean;
  game?: any;
  assessmentDate?: string;
  // False while the persona is still resolving. The slide SET is persona-driven
  // (a CSOC user leads with CLASS TODAY, a self-learner with REVIEW GAME), so
  // rendering before it resolves flashed the wrong banner for ~1-2s.
  personaReady?: boolean;
}

const BannerCarousel = ({
  isBeginnerPlayer,
  game,
  assessmentDate,
  personaReady = true,
}: BannerCarouselProps) => {
  const {isMobileOrTab} = useMediaQuery();
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeBanner, setActiveBanner] = useState(0);
  const carouselRef = useRef<any>(null);
  const navigation = useNavigation<any>();
  const {data: nextTournamentResp} = useGetNextMasteryTournament();
  const nextTournamentId = nextTournamentResp?.tournament?.id;
  const userId = useLoggedInUserStore(state => state.userId);
  const {data: nextArena} = useGetUpcomingArena(userId);
  const {data: royaleTournaments} = useRoyaleTournaments(['scheduled', 'live']);
  // Soonest scheduled/live Puzzle Royale — a live one (scheduled_at may be in
  // the past or null) always sorts first since a missing/past time collapses
  // to 0 while a genuinely upcoming one is a positive epoch ms.
  const nextRoyale = useMemo(() => {
    const candidates = (royaleTournaments || []).filter(
      t => t.state === 'scheduled' || t.state === 'live',
    );
    if (!candidates.length) return null;
    return [...candidates].sort((a, b) => {
      const aTime = a.state === 'live' ? 0 : a.scheduled_at ? new Date(a.scheduled_at).getTime() : Infinity;
      const bTime = b.state === 'live' ? 0 : b.scheduled_at ? new Date(b.scheduled_at).getTime() : Infinity;
      return aTime - bTime;
    })[0];
  }, [royaleTournaments]);
  // Real daily-wins data (same feed the pinned rail/Live-now use) for the
  // "Daily Leaderboard" info slide — no invented numbers here either.
  const {rows: lbRows, me: lbMe, totalPlayers: lbTotal, isLoading: lbLoading} =
    useLeaderboardData('daily', {refetchIntervalMs: 60000});

  const banners = useMemo<DynamicBannerConfig[]>(() => {
    const list = DASHBOARD_BANNERS.map(b => {
      if (b.id === 'masterclass_opening' && nextTournamentId) {
        return {
          ...b,
          onPress: () => {
            Linking.openURL(
              `https://learn.circlechess.com/playChess?tournamentId=${nextTournamentId}`,
            ).catch(err => console.error('Failed to open URL:', err));
          },
        };
      }
      return b;
    });

    // Daily Arena — a rich campaign banner (own component/render fn), not a
    // slim info slide. Shown only when there is an upcoming or
    // currently-running arena; drops out otherwise. Copy comes from
    // ARENA_CAMPAIGN while that live event is on, else falls back to the
    // evergreen ARENA_GENERIC copy.
    const arenaSlide: DynamicBannerConfig | null = nextArena
      ? (() => {
          const arenaCopy = ARENA_CAMPAIGN.enabled ? ARENA_CAMPAIGN : ARENA_GENERIC;
          const startMs = new Date(nextArena.start_date).getTime();
          const isRunning = Date.now() >= startMs;
          return {
            id: 'arena_campaign',
            variant: 'campaign',
            render: () => (
              <CampaignBanner
                palette={WINE}
                kicker={arenaCopy.kicker}
                freeLabel={arenaCopy.freeLabel}
                title={arenaCopy.title}
                titleAccent={arenaCopy.titleAccent}
                subtitle={arenaCopy.subtitle}
                detailsText={arenaCopy.detailsText || undefined}
                onDetails={
                  arenaCopy.detailsUrl
                    ? () =>
                        Linking.openURL(arenaCopy.detailsUrl).catch(err =>
                          console.error('Failed to open URL:', err),
                        )
                    : undefined
                }
                countdownTarget={startMs}
                live={isRunning}
                ctaText={arenaCopy.ctaText}
                ctaSubtext={arenaCopy.ctaSubtext || undefined}
                isMobile={isMobileOrTab}
                onPress={() =>
                  navigation.navigate(ScreenName.PlayChess, {
                    tournamentId: String(nextArena.id),
                    tournamentName: nextArena.name,
                  })
                }
              />
            ),
          };
        })()
      : null;

    // Puzzle Royale — the campaign banner shown when there's no arena (or
    // alongside it, as a second rotating slide). The durable ROYALE route is
    // a separate id space from chess tournaments — `royaleTournamentId`, NOT
    // `tournamentId` (skill.md pitfall 34; see TournamentData.tsx).
    const royaleSlide: DynamicBannerConfig | null = nextRoyale
      ? {
          id: 'royale_campaign',
          variant: 'campaign',
          render: () => (
            <CampaignBanner
              palette={INK_GOLD}
              kicker={PUZZLE_ROYALE.kicker}
              freeLabel={PUZZLE_ROYALE.freeLabel}
              title={PUZZLE_ROYALE.title}
              titleAccent={PUZZLE_ROYALE.titleAccent}
              subtitle={PUZZLE_ROYALE.subtitle}
              detailsText={PUZZLE_ROYALE.detailsText || undefined}
              countdownTarget={
                nextRoyale.scheduled_at
                  ? new Date(nextRoyale.scheduled_at).getTime()
                  : null
              }
              live={nextRoyale.state === 'live'}
              ctaText={PUZZLE_ROYALE.ctaText}
              ctaSubtext={PUZZLE_ROYALE.ctaSubtext || undefined}
              isMobile={isMobileOrTab}
              onPress={() =>
                navigation.navigate(ScreenName.PuzzleRoyaleTournament, {
                  royaleTournamentId: nextRoyale.id,
                })
              }
            />
          ),
        }
      : null;

    const hasGame = !!game?.uuid;
    const review: DynamicBannerConfig | null = hasGame
      ? {
          id: 'review_game',
          variant: 'info',
          icon: dsIcons.gameAnalytics,
          kicker: 'REVIEW GAME',
          title: `Your game vs ${game.opponent} (${game.opponent_rating}) is ready`,
          subTitle: `Accuracy ${
            Number(game.accuracy) ? Number(game.accuracy).toFixed(1) : '—'
          } · ${game?.score?.no_of_blunders ?? 0} blunders committed`,
          buttonText: 'Review',
          backgroundColor: '#4D3F37',
          onPress: () =>
            navigation.navigate(ScreenName.ChessAnalysis, {
              game_id: game.uuid,
              analyzed: game?.analyzed,
            }),
        }
      : null;

    const assess: DynamicBannerConfig | null = assessmentDate
      ? {
          id: 'run_assessment',
          variant: 'info',
          icon: dsIcons.assessment,
          kicker: 'ASSESSMENT',
          title: 'Run your next assessment',
          subTitle: `Last updated on ${assessmentDate}`,
          buttonText: 'Run',
          backgroundColor: '#7A5C46',
          onPress: () => navigation.navigate(ScreenName.Assessments),
        }
      : null;

    const leader = lbRows[0];
    const board: DynamicBannerConfig | null =
      !lbLoading && leader
        ? {
            id: 'daily_leaderboard',
            variant: 'info',
            icon: dsIcons.tournament,
            kicker: 'DAILY LEADERBOARD',
            live: true,
            title: `${leader.name} leads with ${leader.wins} wins today`,
            subTitle: lbMe?.rank
              ? `You are #${lbMe.rank} of ${lbTotal} players`
              : `You are unranked of ${lbTotal} players`,
            buttonText: 'View',
            backgroundColor: '#5C6E62',
            onPress: () => navigation.navigate(ScreenName.Leaderboard),
          }
        : null;

    const firstGame: DynamicBannerConfig | null = isBeginnerPlayer
      ? {
          id: 'first_game',
          variant: 'info',
          icon: dsIcons.gamePlay,
          kicker: 'GET STARTED',
          title: 'Play your first game to unlock rewards',
          subTitle: 'Goals, assessment and points open up after it',
          buttonText: 'Start',
          backgroundColor: '#4D3F37',
          onPress: () => navigation.navigate(ScreenName.PlayChess),
        }
      : null;

    // Neither class detail nor Play is a banner any more — both are cards now
    // (class beside Assignments, Play in the shortcut grid with its own time
    // control), so you don't wait for a slide to come round to reach either.
    // What's left here is genuinely rotational: things worth a look, not things
    // you came to do.
    // For now the top carousel holds ONLY the campaign banners — Daily Arena
    // and Puzzle Royale. Both live → they rotate; either can drop out on its
    // own (no arena today, or no scheduled/live royale); neither → carousel
    // hides. (Persona/info slides intentionally excluded for now — this
    // supersedes the temporary ARENA_PROMO_ONLY pin.)
    return [arenaSlide, royaleSlide].filter(Boolean) as DynamicBannerConfig[];
  }, [
    nextTournamentId,
    nextArena?.id,
    nextArena?.name,
    nextArena?.start_date,
    nextRoyale?.id,
    nextRoyale?.scheduled_at,
    nextRoyale?.state,
    isMobileOrTab,
    lbRows,
    lbMe,
    lbTotal,
    lbLoading,
    navigation,
    isBeginnerPlayer,
    game,
    assessmentDate,
  ]);

  const onLayout = (event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width > 0) {
      setContainerWidth(event.nativeEvent.layout.width);
    }
  };

  const handleCopyCoupon = (config: DynamicBannerConfig) => {
    if (!config?.couponCode) return;
    Clipboard.setString(config.couponCode);
    config.onCopyCoupon?.();
  };

  const renderTags = (config: DynamicBannerConfig) => {
    const activeTags = isMobileOrTab ? config?.tagsMobile : config?.tags;
    if (!activeTags?.length) return null;

    if (config.variant === 'feature') {
      return (
        <View style={styles.chipsContainer}>
          {activeTags.map((tag, index) => (
            <View key={index} style={styles.chip}>
              <CCText style={styles.chipText}>{tag}</CCText>
            </View>
          ))}
        </View>
      );
    }

    return (
      <View style={styles.tagsContainer}>
        {activeTags.map((tag, index) => (
          <React.Fragment key={index}>
            <CCText
              fontSize={isMobileOrTab ? FontSizes[12] : FontSizes[14]}
              color={CCColors?.White}
              style={styles.tag}>
              {tag}
            </CCText>
            {index < activeTags.length - 1 && (
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors?.White}
                style={styles.tagSeparator}>
                •
              </CCText>
            )}
          </React.Fragment>
        ))}
      </View>
    );
  };

  const renderCoupon = (config: DynamicBannerConfig) => {
    if (!config?.couponCode) return null;
    return (
      <TouchableOpacity
        style={styles.couponContainer}
        onPress={() => handleCopyCoupon(config)}
        activeOpacity={0.8}>
        <Box style={styles.couponContainerInner}>
          <CCText style={styles.couponText}>{config?.couponCode}</CCText>
          <MaterialCommunityIcon
            name="content-copy"
            size={16}
            color={CCColors?.TextColor?.Dark}
          />
        </Box>
      </TouchableOpacity>
    );
  };

  const renderOffer = (config: DynamicBannerConfig) => {
    if (!config?.offerText) return null;
    return (
      <View style={styles.offerContainer}>
        <CCText style={styles.upToText}>UP TO</CCText>
        <CCText style={styles.offText}>
          {config?.offerText?.replace('UP TO ', '')}
        </CCText>
      </View>
    );
  };

  const renderCheckmarks = (config: DynamicBannerConfig) => {
    if (!config?.checkmarks?.length) return null;
    return (
      <View style={styles.checkmarksContainer}>
        {config?.checkmarks?.map((text, i) => (
          <View key={i} style={styles.checkmarkRow}>
            <FontAwesome5 name="check-circle" size={14} color="#2DD4BF" solid />
            <CCText style={styles.checkmarkText}>{text}</CCText>
          </View>
        ))}
      </View>
    );
  };

  const renderChessPiecesBackground = (config: DynamicBannerConfig) => {
    if (config?.variant !== 'feature') return null;
    const scale = isMobileOrTab ? 0.45 : 1;
    return (
      <View style={styles.chessPiecesOverlay} pointerEvents="none">
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 20 * scale,
              left: '5%',
              width: 120 * scale,
              height: 160 * scale,
              transform: [{rotate: '-15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Bishop.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -40 * scale,
              left: '20%',
              width: 160 * scale,
              height: 220 * scale,
              transform: [{rotate: '25deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Rook.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 30 * scale,
              left: '38%',
              width: 110 * scale,
              height: 160 * scale,
              transform: [{rotate: '-10deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Knight.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: -20 * scale,
              left: '55%',
              width: 150 * scale,
              height: 200 * scale,
              transform: [{rotate: '-20deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Queen.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -15 * scale,
              left: '72%',
              width: 140 * scale,
              height: 200 * scale,
              transform: [{rotate: '15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-King.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 10 * scale,
              left: '88%',
              width: 140 * scale,
              height: 190 * scale,
              transform: [{rotate: '30deg'}],
            },
          ]}
        />
      </View>
    );
  };

  const renderBannerItem = ({item}: {item: DynamicBannerConfig}) => {
    const config = item;
    const bgStyle = config?.backgroundColor
      ? {backgroundColor: config?.backgroundColor}
      : {};

    if (config.variant === 'campaign') {
      // Give the slide an explicit height so every campaign banner fills it
      // (via CampaignBanner's flex:1) — a shorter slide like Puzzle Royale then
      // matches the taller arena slide instead of leaving a gap under it.
      return (
        <View style={{height: carouselHeight}}>{config.render?.() ?? null}</View>
      );
    }

    if (config.variant === 'info') {
      return (
        <View
          style={[
            styles.leaderboardBanner,
            {backgroundColor: config.backgroundColor || '#4D3F37'},
          ]}>
          <View style={styles.leaderboardBannerLeft}>
            <View style={styles.leaderboardBannerIcon}>
              <Image
                source={config.icon || dsIcons.gamePlay}
                style={{width: 23, height: 23}}
                resizeMode="contain"
              />
            </View>
            <View style={{flex: 1}}>
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                {config.live && <RadarDot color="#FACF47" size={7} />}
                <CCText
                  fontSize={11}
                  fontWeight="bold"
                  color="rgba(255,255,255,0.7)"
                  style={{letterSpacing: 0.5}}>
                  {config.kicker}
                </CCText>
              </View>
              <CCText
                fontSize={isMobileOrTab ? 14 : 16}
                fontWeight="bold"
                color="#fff"
                style={{marginTop: 2}}
                numberOfLines={1}>
                {config.title}
              </CCText>
              <CCText
                fontSize={12.5}
                color="rgba(255,255,255,0.75)"
                style={{marginTop: 2}}>
                {config.subTitle}
              </CCText>
            </View>
          </View>
          {/* Pressable rather than TouchableOpacity: this is the banner's only
              action, and it needs a hover state to read as clickable on web. */}
          <Pressable
            style={({hovered, pressed}: any) => [
              styles.leaderboardBannerBtn,
              {cursor: 'pointer'},
              hovered && styles.leaderboardBannerBtnHovered,
              pressed && {opacity: 0.9},
            ]}
            onPress={() => config?.onPress?.()}>
            {({hovered}: any) => (
              <>
                <Shine travel={110} width={32} interval={3000} delay={400} />
                <View style={styles.bannerBtnRow}>
                  <CCText fontWeight="bold" fontSize={13.5} color="#1F2937">
                    {config.buttonText}
                  </CCText>
                  <NudgeArrow hovered={hovered} fontSize={13.5} color="#1F2937" />
                </View>
              </>
            )}
          </Pressable>
        </View>
      );
    }

    if (isMobileOrTab) {
      return (
        <Box style={[styles.mobileContainer, bgStyle]}>
          {config?.variant === 'feature' && <ShimmerOverlay />}
          {renderChessPiecesBackground(config)}

          {/* Mobile Image Layer (Rendered First to Sit in Background) */}
          {config?.mainImageMobile && config?.variant === 'feature' ? (
            <View style={styles.mobileFeatureMainImageContainer}>
              <Image
                source={require('@/Assets/ChessPieces/White-Pawn.svg')}
                style={[
                  styles.overlappingPieceLeft,
                  {transform: [{scale: 0.65}], left: -35, top: -20},
                ]}
              />
              <Image
                source={require('@/Assets/ChessPieces/White-Rook.svg')}
                style={[
                  styles.overlappingPieceRight,
                  {transform: [{scale: 0.65}], right: -35, bottom: -25},
                ]}
              />
              <Image
                source={config?.mainImageMobile}
                style={styles.mobileFeatureMainImage}
              />
            </View>
          ) : config.mainImageMobile ? (
            <Image
              source={config?.mainImageMobile}
              style={styles.mobileGirlImage}
            />
          ) : null}

          <View
            style={[
              styles.mobileLeftContent,
              config?.variant === 'feature' && {maxWidth: '90%', zIndex: 10},
            ]}>
            {config?.variant === 'feature' && config?.logo ? (
              <View
                style={[
                  styles.featureTopRow,
                  {flexWrap: 'nowrap', marginBottom: 4},
                ]}>
                <Image
                  source={config?.logo}
                  style={[styles.featureLogo, {width: 80, height: 16}]}
                  resizeMode="contain"
                />
                {config?.subTitle && (
                  <View
                    style={[
                      styles.subTitleBadge,
                      {
                        paddingHorizontal: 4,
                        paddingVertical: 2,
                        marginLeft: 4,
                        flexShrink: 1,
                      },
                    ]}>
                    <CCText
                      style={[styles.subTitleBadgeText, {fontSize: 8}]}
                      numberOfLines={1}>
                      {config?.subTitle}
                    </CCText>
                  </View>
                )}
              </View>
            ) : (
              (config?.logo || config?.couponCode) && (
                <View
                  style={[styles.logoContainer, styles.mobileLogoContainer]}>
                  {config?.logo && (
                    <Image source={config?.logo} style={styles.logo} />
                  )}
                  {renderCoupon(config)}
                </View>
              )
            )}

            {config?.variant !== 'feature' && config?.subTitle && (
              <CCText style={styles.subTitle}>{config?.subTitle}</CCText>
            )}

            {config.title && (
              <CCText
                style={[
                  styles.title,
                  styles.mobileTitle,
                  config.variant === 'feature' && styles.featureTitle,
                ]}>
                {config.title}
              </CCText>
            )}

            {renderCheckmarks(config)}
            {renderTags(config)}

            {config.variant === 'feature' ? (
              <TouchableOpacity
                style={{
                  marginTop: 6,
                  alignSelf: 'flex-start',
                  backgroundColor: '#FDE68A',
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 100,
                  zIndex: 100,
                }}
                onPress={() => config?.onPress?.()}>
                <CCText
                  style={{color: '#1F2937', fontWeight: '800', fontSize: 13}}>
                  {config?.priceText} - {config?.buttonText}
                </CCText>
              </TouchableOpacity>
            ) : (
              <View style={styles.mobileBottomRow}>
                {config.priceText && (
                  <View style={styles.priceTextContainer}>
                    <CCText style={styles.priceText}>
                      {config?.priceText}
                    </CCText>
                  </View>
                )}
                {config.buttonText && (
                  <CCButton
                    style={{marginTop: 5}}
                    onPress={() => config?.onPress?.()}>
                    <CCText style={styles.btnText}>{config?.buttonText}</CCText>
                  </CCButton>
                )}
              </View>
            )}
          </View>
        </Box>
      );
    }

    // Web / Desktop
    return (
      <Box
        style={[
          styles.container,
          bgStyle,
          config?.variant !== 'feature' && {
            paddingVertical: 0,
            paddingBottom: 0,
          },
        ]}>
        {/* Shimmer Effect overlay in the background */}
        {config.variant === 'feature' && <ShimmerOverlay />}

        {/* Feature banner: decorative chess pieces background */}
        {renderChessPiecesBackground(config)}

        <View
          style={[
            styles.leftContent,
            config?.variant === 'feature'
              ? isMobileOrTab
                ? {maxWidth: '62%', paddingVertical: 10}
                : {maxWidth: '50%'}
              : {paddingVertical: 10},
          ]}>
          {config?.variant === 'feature' && config?.logo && (
            <View
              style={[
                styles.featureTopRow,
                isMobileOrTab && {flexWrap: 'nowrap', marginBottom: 4},
              ]}>
              <Image
                source={config?.logo}
                style={[
                  styles.featureLogo,
                  isMobileOrTab && {width: 80, height: 16},
                ]}
                resizeMode="contain"
              />
              {config?.subTitle && (
                <View
                  style={[
                    styles.subTitleBadge,
                    isMobileOrTab && {
                      paddingHorizontal: 4,
                      paddingVertical: 2,
                      marginLeft: 4,
                      flexShrink: 1,
                    },
                  ]}>
                  <CCText
                    style={[
                      styles.subTitleBadgeText,
                      isMobileOrTab && {fontSize: 8},
                    ]}
                    numberOfLines={1}>
                    {config?.subTitle}
                  </CCText>
                </View>
              )}
            </View>
          )}

          {config?.variant !== 'feature' && config?.subTitle && (
            <CCText style={styles.subTitle}>{config?.subTitle}</CCText>
          )}

          {config.title && (
            <CCText
              fontSize={
                config?.variant === 'feature' ? FontSizes[18] : FontSizes[24]
              }
              style={[
                styles.title,
                config?.variant === 'feature' && styles.featureTitle,
              ]}>
              {config?.title}
            </CCText>
          )}

          {renderCheckmarks(config)}
          {renderTags(config)}

          {config?.variant !== 'feature' && (
            <View style={styles.bottomRow}>
              {config?.priceText && (
                <View style={styles.priceTextContainer}>
                  <CCText style={styles.priceText}>{config?.priceText}</CCText>
                </View>
              )}
              {config?.buttonText && (
                <CCButton onPress={() => config?.onPress?.()}>
                  <CCText style={styles.btnText}>{config?.buttonText}</CCText>
                </CCButton>
              )}
              {renderOffer(config)}
              {renderCoupon(config)}
            </View>
          )}

          {config.variant === 'feature' && isMobileOrTab && (
            <TouchableOpacity
              style={{
                marginTop: 6,
                alignSelf: 'flex-start',
                backgroundColor: '#FDE68A',
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 100,
                zIndex: 100,
              }}
              onPress={() => config?.onPress?.()}>
              <CCText
                style={{color: '#1F2937', fontWeight: '800', fontSize: 13}}>
                {config?.priceText} - {config?.buttonText}
              </CCText>
            </TouchableOpacity>
          )}
        </View>

        {/* Feature banner: right action area with price + CTA */}
        {config?.variant === 'feature' && !isMobileOrTab && (
          <View style={styles.rightActionContainer}>
            {config?.priceText && (
              <View style={styles.featurePriceBlock}>
                <CCText style={styles.featurePriceAmount}>
                  {config?.priceText}
                </CCText>
              </View>
            )}

            {config?.buttonText && (
              <TouchableOpacity
                style={styles.featureEnrollBtn}
                onPress={() => config?.onPress?.()}
                activeOpacity={0.8}>
                <CCText style={styles.featureEnrollBtnText}>
                  {config.buttonText}
                </CCText>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Promotional: logo overlay */}
        {config?.variant !== 'feature' && config?.logo && (
          <View
            style={{
              position: 'absolute',
              top: 20,
              left: '50%',
              transform: [{translateX: 50}],
            }}>
            <Image source={config.logo} style={styles.logo} />
          </View>
        )}

        {/* Main image / Feature image with overlapping chess pieces */}
        {config?.mainImage && config.variant === 'feature' ? (
          <View
            style={
              isMobileOrTab
                ? styles.mobileFeatureMainImageContainer
                : styles.featureMainImageContainer
            }>
            <Image
              source={require('@/Assets/ChessPieces/White-Pawn.svg')}
              style={
                isMobileOrTab
                  ? [
                      styles.overlappingPieceLeft,
                      {transform: [{scale: 0.65}], left: -35, top: -20},
                    ]
                  : styles.overlappingPieceLeft
              }
            />
            <Image
              source={require('@/Assets/ChessPieces/White-Rook.svg')}
              style={
                isMobileOrTab
                  ? [
                      styles.overlappingPieceRight,
                      {transform: [{scale: 0.65}], right: -35, bottom: -25},
                    ]
                  : styles.overlappingPieceRight
              }
            />
            <Image
              source={
                isMobileOrTab && config?.mainImageMobile
                  ? config?.mainImageMobile
                  : config?.mainImage
              }
              style={
                isMobileOrTab
                  ? styles.mobileFeatureMainImage
                  : styles.featureMainImage
              }
            />
          </View>
        ) : config.mainImage ? (
          <Image source={config.mainImage} style={styles.girlImage} />
        ) : null}
      </Box>
    );
  };

  const hasBanners = !!banners && banners.length > 0;
  // Placeholder while the persona resolves (the slide SET is persona-driven) or
  // before the first width measurement.
  const showPlaceholder = !personaReady || !hasBanners;
  const hasMultipleBanners = banners?.length > 1;
  // Looping needs at least 3 slides. With exactly 2 (the CSOC set is
  // [classToday, review]) reanimated-carousel has to reuse a slide as both the
  // previous and next neighbour, so mid-transition it renders two side by side
  // and you see the tail of one banner next to the head of the other. Autoplay
  // still cycles without loop — it just bounces back instead of wrapping.
  const canLoop = (banners?.length || 0) > 2;
  // The persona info slides are a compact strip (design: 14px/16px padding
  // around a 40px icon row ≈ 76px tall), nothing like the 220px promo/feature
  // artwork banners. Size the carousel to whatever it's actually showing.
  const allInfo = hasBanners && banners.every(b => b.variant === 'info');
  // The campaign banners (Daily Arena / Puzzle Royale) are a mid-height strip
  // — taller than the compact info slides, shorter than the artwork
  // promo/feature banners — so they get their own height bracket.
  const hasCampaign = hasBanners && banners.some(b => b.variant === 'campaign');
  const carouselHeight = hasCampaign
    ? isMobileOrTab
      ? 340
      : 214
    : allInfo
    ? isMobileOrTab
      ? 92
      : 80
    : isMobileOrTab
    ? 200
    : 220;

  return (
    <View style={styles.wrapper} onLayout={onLayout}>
      {showPlaceholder && <View style={styles.infoSkeleton} />}
      {/* Item width must be the wrapper's real width or slides tile side by
          side, so wait for the first measurement instead of guessing. */}
      {!showPlaceholder && containerWidth > 0 && (
      <Carousel
        ref={carouselRef}
        loop={canLoop}
        width={containerWidth}
        height={carouselHeight}
        autoPlay={hasMultipleBanners}
        autoPlayInterval={
          banners[activeBanner]?.variant === 'campaign'
            ? 9000
            : allInfo
            ? 4500
            : 10000
        }
        data={banners}
        renderItem={renderBannerItem}
        scrollAnimationDuration={1000}
        onSnapToItem={setActiveBanner}
        enabled={false}
      />
      )}
      {/* Design shows dot pagination under the info strip; the arrow overlay
          belongs to the tall artwork banners and would sit on top of the copy
          at 80px. Campaign banners get dots too — an arrow overlay centered
          on the slide's edge would sit right on top of the CTA/countdown. */}
      {!showPlaceholder && hasMultipleBanners && (allInfo || hasCampaign) && (
        <View style={styles.dotsRow}>
          {banners.map((b, i) => (
            <TouchableOpacity
              key={b.id}
              onPress={() => carouselRef?.current?.scrollTo({index: i, animated: true})}
              style={[
                styles.dot,
                i === activeBanner ? styles.dotActive : styles.dotIdle,
              ]}
            />
          ))}
        </View>
      )}
      {!showPlaceholder && hasMultipleBanners && !allInfo && !hasCampaign && (
        <>
          <TouchableOpacity
            style={styles.leftArrow}
            onPress={() => carouselRef?.current?.prev()}>
            <FontAwesome5
              name="chevron-left"
              size={isMobileOrTab ? 16 : 24}
              color="#FFF"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.rightArrow}
            onPress={() => carouselRef?.current?.next()}>
            <FontAwesome5
              name="chevron-right"
              size={isMobileOrTab ? 16 : 24}
              color="#FFF"
            />
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

export default memo(BannerCarousel);
